import React, { useCallback, useEffect, useState } from "react";
import { LogOut, Inbox, Car, LayoutDashboard, RefreshCw, FolderArchive } from "lucide-react";
import { api } from "@/lib/api";
import AdminDashboard from "@/components/AdminDashboard";
import AdminVehicles from "@/components/AdminVehicles";
import AdminLeads from "@/components/AdminLeads";
import AdminSync from "@/components/AdminSync";
import AdminAccounting from "@/components/accounting/AdminAccounting";
import { inputCls } from "@/components/InquiryForm";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("af_admin_token", data.access_token);
      onLogin();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Anmeldung fehlgeschlagen");
    }
    setLoading(false);
  };

  return (
    <div className="pt-36 pb-24 max-w-md mx-auto px-5" data-testid="admin-login">
      <p className="eyebrow">Händlerbereich</p>
      <h1 className="font-display font-bold text-3xl text-ink mt-5 mb-8">Admin-Login</h1>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="admin-email-input" type="email" required placeholder="E-Mail" value={email}
          onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="admin-password-input" type="password" required placeholder="Passwort" value={password}
          onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
        <button data-testid="admin-login-btn" type="submit" disabled={loading}
          className="btn-pill w-full bg-action text-action-ink hover:bg-action-hover font-semibold py-4 text-sm disabled:opacity-50">
          {loading ? "Anmeldung…" : "Anmelden"}
        </button>
      </form>
    </div>
  );
};

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "fahrzeuge", label: "Fahrzeuge", icon: Car },
  { id: "sync", label: "Synchronisierung", icon: RefreshCw },
  { id: "leads", label: "Anfragen", icon: Inbox },
  { id: "belege", label: "Buchhaltung & Belege", icon: FolderArchive },
];

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [leads, setLeads] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const load = useCallback(async () => {
    try {
      const [l, vs] = await Promise.all([api.get("/admin/leads"), api.get("/admin/vehicles")]);
      setLeads(l.data);
      setVehicles(vs.data);
      setAuthed(true);
    } catch {
      localStorage.removeItem("af_admin_token");
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("af_admin_token")) load();
    else setAuthed(false);
  }, [load]);

  const logout = () => {
    localStorage.removeItem("af_admin_token");
    setAuthed(false);
  };

  if (authed === null)
    return <div className="min-h-[60vh] flex items-center justify-center pt-20"><div className="w-8 h-8 border-2 border-line border-t-brand rounded-full animate-spin" /></div>;
  if (!authed) return <Login onLogin={load} />;

  const counts = { fahrzeuge: vehicles.length, leads: leads.length };

  return (
    <div data-testid="admin-dashboard" className="pt-24 md:pt-32 pb-24 max-w-6xl mx-auto px-5 sm:px-8">
      <div className="flex items-center justify-between gap-4 mb-10">
        <div>
          <p className="eyebrow">Händlerbereich</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-ink mt-4">Admin</h1>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout}
          className="btn-pill inline-flex items-center gap-2 border border-line text-ink px-5 py-2.5 text-sm font-semibold hover:border-silver transition-colors">
          <LogOut size={14} /> Abmelden
        </button>
      </div>

      <div className="flex gap-2 mb-8 flex-wrap" role="tablist">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} role="tab" aria-selected={tab === id}
            className={`btn-pill inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-action text-action-ink" : "border border-line text-dim hover:text-ink hover:border-silver"}`}>
            <Icon size={15} /> {label}{counts[id] !== undefined && ` (${counts[id]})`}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <AdminDashboard onOpenSync={() => setTab("sync")} />}
      {tab === "fahrzeuge" && <AdminVehicles vehicles={vehicles} reload={load} />}
      {tab === "sync" && <AdminSync onSynced={load} />}
      {tab === "leads" && <AdminLeads leads={leads} />}
      {tab === "belege" && <AdminAccounting vehicles={vehicles} />}
    </div>
  );
}
